import {useEffect, useState} from "react"
import {
	Box, Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Switch,
	Tooltip,
	Typography
} from "@mui/material"
import {useDispatch, useSelector} from "react-redux"
import {
	clearManageWorkspaceView,
	fetchMemberUserToModify, MANAGE_WORKSPACE_VIEW, selectIsAddingNewMember,
	selectTargetUser, setManageWorkspaceView, setTargetUser
} from "../../reducers/manageWorkspaceSlice"
import {ManageModalContainer} from "./shared_components/ManageModalContainer"

export default function ConfigureWorkspacePermissions() {
	const [read, setRead] = useState(true) // is there any reason read would ever be set to false??
	const [write, setWrite] = useState(false)
	const [admin, setAdmin] = useState(false)
	const [isFreeTier, setIsFreeTier] = useState(false)
	const [showFreeTierExplainer, setShowFreeTierExplainer] = useState(false)
	const isAddingNewMember = useSelector(selectIsAddingNewMember)
	const targetUser = useSelector(selectTargetUser)
	const dispatch = useDispatch()

	const member = useSelector(selectTargetUser)

	const resetModal = () => {
		dispatch(clearManageWorkspaceView())
	}

	const handleSubmit = async () => {
		// clone existing MemberDTO obj
		const modifiedMember = { ...member}
		// modify the clone with the updated settings
		modifiedMember.workspacePermissions = {read, write, admin}
		// set the **LOCAL** target user to the modified version, and move to note permissions
		dispatch(setTargetUser(modifiedMember))
			.then(() => {
				dispatch(setManageWorkspaceView(MANAGE_WORKSPACE_VIEW.NOTE_PERMISSIONS))
			})
	}

	useEffect(() => {
		if(isAddingNewMember) {
			// if we are adding a new member, we don't need to consider either the existing workspace permissions
			// or the existing note restrictions...
			setIsFreeTier(targetUser.user.isFreeTier === true || targetUser.user.ownsSubscription === false)
			setRead(true)
			setWrite(false)
			setAdmin(false)
			setShowFreeTierExplainer(false)
		} else {
			dispatch(fetchMemberUserToModify(member))
				.then((combinedUserObj) => {
					const memberToMod = combinedUserObj.payload
					if(memberToMod) {
						setIsFreeTier(memberToMod.isFreeTier)
						setRead(memberToMod.workspacePermissions.read)
						setWrite(memberToMod.isFreeTier ? false : memberToMod.workspacePermissions.write)
						setAdmin(memberToMod.isFreeTier ? false : memberToMod.workspacePermissions.admin)
						if(memberToMod.isFreeTier && memberToMod.workspacePermissions.write === true) {
							setShowFreeTierExplainer(true)
						}
					}
				})
				.catch(e => {
					console.error("FROM CONFIGURE WORKSPACE PERMISSIONS: DISPATCHED ACTION IN USE EFFECT FAILED, ", e)
				})
		}
	}, [])

	return (
		<ManageModalContainer>
			<DialogTitle>Configure Workspace Permissions</DialogTitle>
			<DialogContent>
				{ member &&
					<Box
						sx={{ margin: "auto", display: "flex", flexDirection: "column" }}
					>
						<Box sx={{ textAlign: "center" }}>
							<Typography variant={"subtitle2"}>
								{member.user.firstName} {member.user.lastName}{" "}
							</Typography>
							<Typography variant={"caption"}>( {member.user.email} )</Typography>
							{ showFreeTierExplainer &&
								<Typography variant={"caption"} color={ "alertRed.mainTransparency100" } paragraph>
									This user was previously given permission to edit notes and articles in this workspace. However, their paid subscription has lapsed since then. Only users with active Refzoom subscriptions can be given permission to write inside workspaces they do not own.
								</Typography>
							}
						</Box>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center"
							}}
						>
							<FormControlLabel
								control={
									<Switch
										checked={read}
										onChange={(e) => setRead(e.target.checked)}
										name="Read"
									/>
								}
								label="Read Access"
							/>
							<Tooltip
								title={isFreeTier && "Free users cannot write or admin workspaces they don't own"}
								placement={"right"}
								arrow
							>
								<span>
									<FormControlLabel
										control={
											<Switch
												checked={write}
												onChange={(e) => setWrite(e.target.checked)}
												name="Write"
												disabled={isFreeTier}
											/>
										}
										label="Write Access"
									/>
								</span>
							</Tooltip>
							<Tooltip
								title={isFreeTier && "Free users cannot write or admin workspaces they don't own"}
								placement={"right"}
								arrow
							>
								<span>
									<FormControlLabel
										control={
											<Switch
												checked={admin}
												onChange={(e) => setAdmin(e.target.checked)}
												name="Admin"
												disabled={isFreeTier}
											/>
										}
										label="Admin Access"
									/>
								</span>
							</Tooltip>
						</Box>
						{ (!member.admin && admin) &&
							<Typography variant={"body2"} color={ "alertRed.mainTransparency100" }>
								Warning: Giving a user admin access to this workspace will make it impossible for you to remove them from this workspace, and will give them full management permissions. This action is irreversible and permission is irrevocable without contacting support.
							</Typography>
						}
					</Box>
				}
			</DialogContent>
			<DialogActions sx={{ display: "flex", justifyContent:"space-evenly" }}>
				{member?.admin || (!member?.admin && admin) ?
					<Button
						variant={"outlined"}
						sx={{marginRight: "10px"}}
						onClick={() => handleSubmit([])}
					>
						Submit
					</Button>
					:
					<Button
						variant={"outlined"}
						sx={{marginRight: "10px"}}
						onClick={() => handleSubmit()}
					>
						Next
					</Button>
				}
				<Button
					variant={"outlined"}
					color={"error"}
					onClick={resetModal}
				>
					Cancel
				</Button>
			</DialogActions>
		</ManageModalContainer>
	)
}